/**
 * @fileoverview Toast container that animates toast items.
 *
 * @purpose Render sticky toast notifications with entrance/exit animations.
 * @usage Mounted by `ToastProvider` to show active toast queue.
 * @ownership Shared UI components (core)
 * @dependencies
 * - lucide-react (icons)
 * - react (useEffect/useState)
 * @restrictions
 * - DO NOT duplicate container logic elsewhere; use provider instead.
 */
'use client'
import { AlertCircle, CheckCircle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { Toast } from './types'

const ENTRY_DELAY_MS = 100
const EXIT_ANIMATION_MS = 300
const TOAST_DURATION_MS = 5 * 1000

type ToastProps = {
  toast: Toast
  onRemove: (id: string) => void
}

function ToastItem({ toast, onRemove }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Trigger animation
    const timer = setTimeout(() => setIsVisible(true), ENTRY_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Auto remove after 5 seconds
    const timer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(() => onRemove(toast.id), EXIT_ANIMATION_MS) // Wait for animation
    }, TOAST_DURATION_MS)
    return () => clearTimeout(timer)
  }, [toast.id, onRemove])

  const handleRemove = () => {
    setIsVisible(false)
    setTimeout(() => onRemove(toast.id), 300)
  }

  const isDestructive = toast.variant === 'destructive' || toast.variant === 'error'

  return (
    <div
      data-testid="toast"
      className={`fixed top-20 right-4 z-50 max-w-sm w-full bg-white rounded-lg shadow-lg border-l-4 p-4 transform transition-all duration-300 ${
        isDestructive 
          ? 'border-red-500' 
          : 'border-green-500'
      } ${
        isVisible 
          ? 'translate-x-0 opacity-100' 
          : 'translate-x-full opacity-0'
      }`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {isDestructive ? (
            <AlertCircle className="h-5 w-5 text-red-500" />
          ) : (
            <CheckCircle className="h-5 w-5 text-green-500" />
          )}
        </div>
        <div className="ml-3 w-0 flex-1">
          <p className={`text-sm font-medium ${
            isDestructive ? 'text-red-800' : 'text-green-800'
          }`}>
            {toast.title}
          </p>
          {toast.description && (
            <p className={`mt-1 text-sm ${
              isDestructive ? 'text-red-600' : 'text-green-600'
            }`}>
              {toast.description}
            </p>
          )}
        </div>
        <div className="ml-4 flex-shrink-0 flex">
          <button
            className={`inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isDestructive 
                ? 'text-red-400 hover:text-red-500 focus:ring-red-500' 
                : 'text-green-400 hover:text-green-500 focus:ring-green-500'
            }`}
            onClick={handleRemove}
          >
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

type ToastContainerProps = {
  toasts: Toast[]
  onRemove: (id: string) => void
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  return (
    <div className="fixed top-20 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onRemove={onRemove}
        />
      ))}
    </div>
  )
}
